<template>
  <div class="device-images">
    <template
      v-if="hasImages"
    >
      <image-field
        :src="currentImage"
        class="device-images__image device-images__image--openable"
        contain
        @onClick="openPreview"
      />

      <!--      Only worth paging when there is more than one photo.-->
      <div
        v-if="images.length > 1"
        class="device-images__controls"
      >
        <v-icon
          class="device-images__arrow"
          @click="showPrevious"
        >
          mdi-chevron-left
        </v-icon>

        <span class="device-images__counter">
          {{ currentIndex + 1 }} / {{ images.length }}
        </span>

        <v-icon
          class="device-images__arrow"
          @click="showNext"
        >
          mdi-chevron-right
        </v-icon>
      </div>
    </template>

    <div
      v-else
      class="device-images__empty"
    >
      {{ translate(`${NS}.no_device_images`) }}
    </div>

    <!-- The photograph full size, with the download the dialog already offers. Preview
         only: the pictures come off the driver's device and are not ours to remove. -->
    <images-preview-dialog
      v-if="imagesPreview.isOpen"
      :show-dialog="imagesPreview.isOpen"
      :images="imagesPreview.images"
      :initial-image-index="imagesPreview.initialIndex"
      :dialog-title="imagesPreview.title"
      :enable-delete="false"
      @close="closeImagesPreview"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ImageField from '@/global/components/fields/ImageField.vue'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import { useImagesPreview } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useImagesPreview'
import { isArray, isObject } from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'
import { IRREGULARITIES_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/config/constants'

const props = defineProps({
  // Either plain URLs or objects carrying one - the device upload shape is not
  // pinned down yet, so both are accepted.
  images: {
    type: Array,
    default: () => ([])
  },
  // What the full-size preview is headed with. The report's own type, the same thing the
  // popup of its sign on the map is headed with.
  title: {
    type: String,
    default: ''
  }
})

const { translate } = useTranslate()

const {
  state: imagesPreview,
  open: openImagesPreview,
  close: closeImagesPreview
} = useImagesPreview()

const currentIndex = ref(0)

const hasImages = computed(() => isArray(props.images) && props.images.length > 0)

const currentImage = computed(() => {
  const image = props.images?.[currentIndex.value]
  return isObject(image) ? (image.url || image.src || image.data || null) : image
})

// A shorter list after a refresh would otherwise leave the index past its end.
watch(() => props.images, () => {
  currentIndex.value = 0
})

/**
 * The whole set full size, opened on whichever photograph is on screen - the dialog pages
 * through them itself, so the one being looked at is where it starts rather than all it
 * holds.
 */
function openPreview () {
  if (!hasImages.value) {
    return
  }

  openImagesPreview({
    images: props.images,
    index: currentIndex.value,
    title: props.title
  })
}

function showPrevious () {
  const total = props.images.length
  currentIndex.value = (currentIndex.value - 1 + total) % total
}

function showNext () {
  currentIndex.value = (currentIndex.value + 1) % props.images.length
}
</script>

<style scoped>
.device-images {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  overflow: hidden;
  background-color: #F9FAFB;
}

.device-images__image {
  flex: 1 1 auto;
  min-height: 0;
}

.device-images__image--openable {
  cursor: pointer;
}

.device-images__controls {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px;
  background-color: #FFFFFF;
  border-top: 1px solid #E5E7EB;
}

.device-images__arrow {
  cursor: pointer;
}

.device-images__counter {
  font-family: 'Satoshi', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  min-width: 48px;
  text-align: center;
}

.device-images__empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #6B7280;
  text-align: center;
  padding: 16px;
}
</style>
